import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

interface NavigationInterceptorContextType {
	// State
	isNavigationBlocked: boolean;
	showDialog: boolean;

	// Functions
	blockNavigation: (shouldBlock: boolean) => void;
	interceptNavigation: ({
		onValidNavigation,
		onBlockedNavigation,
		onConfirmNavigation
	}: {
		onValidNavigation: () => void | Promise<void>;
		onBlockedNavigation?: () => void | Promise<void>;
		onConfirmNavigation?: () => void | Promise<void>;
	}) => void | Promise<void>;
	confirmNavigation: () => void;
	cancelNavigation: () => void;
}

const NavigationInterceptorContext = createContext<
	NavigationInterceptorContextType | undefined
>(undefined);

export const useNavigationInterceptorContext = () => {
	const context = useContext(NavigationInterceptorContext);
	if (!context) {
		throw new Error(
			"useNavigationInterceptor must be used within a NavigationInterceptorProvider"
		);
	}
	return context;
};

interface NavigationInterceptorProviderProps {
	children: React.ReactNode;
}

export const NavigationInterceptorProvider: React.FC<
	NavigationInterceptorProviderProps
> = ({ children }) => {
	const [isNavigationBlocked, setIsNavigationBlocked] = useState(false);
	const [showDialog, setShowDialog] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState<{
		onConfirmNavigation?: () => void | Promise<void>;
	} | null>(null);

	const blockNavigation = useCallback((shouldBlock: boolean) => {
		setIsNavigationBlocked(shouldBlock);
	}, []);

	const interceptNavigation = useCallback(
		({
			onValidNavigation,
			onBlockedNavigation,
			onConfirmNavigation
		}: {
			onValidNavigation: () => void | Promise<void>;
			onBlockedNavigation?: () => void | Promise<void>;
			onConfirmNavigation?: () => void | Promise<void>;
		}) => {
			if (isNavigationBlocked) {
				onBlockedNavigation?.();
				setPendingNavigation({ onConfirmNavigation });
				setShowDialog(true);
				return;
			}

			// Return the handler's promise so async callers can await it and observe a
			// failure (e.g. a rejected save) instead of navigating on a broken state.
			return onValidNavigation();
		},
		[isNavigationBlocked]
	);

	const confirmNavigation = useCallback(() => {
		setIsNavigationBlocked(false);
		setShowDialog(false);

		if (pendingNavigation?.onConfirmNavigation) {
			pendingNavigation.onConfirmNavigation();

			setPendingNavigation(null);
		}
	}, [pendingNavigation]);

	const cancelNavigation = useCallback(() => {
		setIsNavigationBlocked(true);
		setPendingNavigation(null);
		setShowDialog(false);
	}, []);

	const value: NavigationInterceptorContextType = useMemo(
		() => ({
			isNavigationBlocked,
			showDialog,
			blockNavigation,
			interceptNavigation,
			confirmNavigation,
			cancelNavigation
		}),
		[
			isNavigationBlocked,
			showDialog,
			blockNavigation,
			interceptNavigation,
			confirmNavigation,
			cancelNavigation
		]
	);

	return (
		<NavigationInterceptorContext.Provider value={value}>
			{children}
		</NavigationInterceptorContext.Provider>
	);
};
